import React, { useMemo } from "react";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { MONTH_LABELS, CHART_FONT, buildMonthlySeries } from "src/utils/chart/chartUtils";

// 콤보차트는 BarElement 까지 등록해야 막대가 그려짐
ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
);

// 막대/선 각 데이터셋을 구성하는 필드/라벨/색상 정보
export interface SeriesConfig<T> {
	field: keyof T;
	label: string;
	color: string;
}

// 우측(y1) 축 설정. 미지정 시 beginAtZero만 적용된 기본 축을 사용
export interface Y1AxisConfig {
	min?: number;   // 라인 데이터가 있을 때 적용할 최소값 (없으면 0)
	max?: number;   // 최대값 (기본 100)
	suffix?: string; // 눈금에 붙일 접미사 (예: "%")
}

interface Props<T extends { mm: number }> {
	data: T[];
	barSeries: [SeriesConfig<T>, SeriesConfig<T>]; // 막대 2개 (좌측 y축)
	lineSeries: SeriesConfig<T>;                    // 선 1개 (우측 y1축)
	y1?: Y1AxisConfig;
	height?: number;
}

// PRD5020(성능가동율)/PRD5030(시간가동율)/PRD5040(양품율)이 공통으로 사용하는
// 막대 2개 + 선 1개 콤보차트 컴포넌트
export const ComboRateChart = <T extends { mm: number }>({
	data,
	barSeries,
	lineSeries,
	y1,
	height = 450,
}: Props<T>) => {

	const { chartData, chartOptions } = useMemo(() => {
		const [bar1, bar2] = barSeries;
		const bar1Data = buildMonthlySeries(data, bar1.field);
		const bar2Data = buildMonthlySeries(data, bar2.field);
		const lineData = buildMonthlySeries(data, lineSeries.field);
		const hasRateData = lineData.some((v) => v > 0);

		const chartData = {
			labels: MONTH_LABELS,
			datasets: [
				{
					type: "bar" as const,
					label: bar1.label,
					yAxisID: "y",
					data: bar1Data,
					backgroundColor: bar1.color,
					order: 2,
				},
				{
					type: "bar" as const,
					label: bar2.label,
					yAxisID: "y",
					data: bar2Data,
					backgroundColor: bar2.color,
					order: 2,
				},
				{
					type: "line" as const,
					label: lineSeries.label,
					yAxisID: "y1",
					data: lineData,
					borderColor: lineSeries.color,
					backgroundColor: lineSeries.color,
					tension: 0.3,
					pointRadius: 3,
					order: 1,
				},
			],
		};

		// y1(우측) 축: y1 설정이 없으면 beginAtZero만 적용된 기본 축 사용 (PRD5020 패턴)
		const y1Scale = y1
			? {
				type: "linear" as const,
				position: "right" as const,
				min: y1.min !== undefined ? (hasRateData ? y1.min : 0) : undefined,
				max: y1.max ?? 100,
				grid: { drawOnChartArea: false },
				ticks: {
					font: CHART_FONT,
					color: '#000000',
					padding: 10,
					...(y1.suffix ? { callback: (v: number | string) => `${v}${y1.suffix}` } : {}),
				},
			}
			: {
				type: "linear" as const,
				position: "right" as const,
				beginAtZero: true,
				grid: { drawOnChartArea: false },
				ticks: { font: CHART_FONT, color: '#000000', padding: 10 },
			};

		const chartOptions = {
			maintainAspectRatio: false,
			responsive: true,
			layout: {
				padding: { left: 12, right: 12, top: 8, bottom: 4 },
			},
			interaction: { mode: "index" as const, intersect: false },
			plugins: {
				legend: { position: "bottom" as const, labels: { font: CHART_FONT } },
				tooltip: { mode: "index" as const, intersect: false },
			},
			scales: {
				y: {
					type: "linear" as const,
					position: "left" as const,
					beginAtZero: true,
					ticks: { font: CHART_FONT, color: '#000000', padding: 10 },
				},
				y1: y1Scale,
				x: { ticks: { font: CHART_FONT, color: '#000000' } },
			},
		};

		return { chartData, chartOptions };
	}, [data, barSeries, lineSeries, y1]);

	return (
		<div style={{ height: `${height}px` }} className="w-full border border-gray-300 p-2">
			<Chart type="bar" data={chartData} options={chartOptions} />
		</div>
	);
};

export default ComboRateChart;
